import { StateCreator } from 'zustand';

import {
  CUSTOM_FILTER_INSTANCE_ID_SUFFIX,
  DEFAULT_PAGE_SIZE,
} from '../../utils/constants';
import { getTableFilterInstanceId } from '../../utils/filterInstanceId';
import { RemoveFunctions } from '../../utils/types';
import { StateSlices } from '..';

export type TablePaginationSlice = {
  pagination: Partial<{
    [pageFilterInstanceId: string]: Partial<{
      [widgetFilterInstanceId: string]: {
        page: number;
        perPage: number;
      };
    }>;
  }>;
  setPage: (
    pageFilterInstanceId: string,
    widgetFilterInstanceId: string,
    page: number
  ) => void;
  setPerPage: (
    pageFilterInstanceId: string,
    widgetFilterInstanceId: string,
    perPage: number
  ) => void;
  resetPagination: (filterInstanceId: string, defaultPageSize?: number) => void;
  resetAllPagination: () => void;
};

const initialState: RemoveFunctions<TablePaginationSlice> = {
  pagination: {},
};

type PaginationState = TablePaginationSlice['pagination'];

function resetPagePagination(
  pagination: PaginationState,
  pageFilterInstanceId: string
): PaginationState | null {
  const pageEntityPaginations = pagination[pageFilterInstanceId];
  if (pageEntityPaginations == null) {
    return null;
  }

  const updatedEntityPaginations = Object.fromEntries(
    Object.entries(pageEntityPaginations).map(
      ([widgetFilterInstanceId, widgetPagination]) => {
        return [
          widgetFilterInstanceId,
          {
            page: 0,
            perPage: widgetPagination?.perPage ?? DEFAULT_PAGE_SIZE,
          },
        ];
      }
    )
  );

  return {
    ...pagination,
    [pageFilterInstanceId]: {
      ...updatedEntityPaginations,
    },
  };
}

function getBaseCustomPageFilterInstanceId(filterInstanceId: string): string {
  return filterInstanceId.slice(0, -CUSTOM_FILTER_INSTANCE_ID_SUFFIX.length);
}

export function resetPaginationState(
  pagination: PaginationState,
  filterInstanceId: string
): PaginationState {
  const pagePagination = resetPagePagination(pagination, filterInstanceId);
  if (pagePagination != null) {
    return pagePagination;
  }

  if (filterInstanceId.endsWith(CUSTOM_FILTER_INSTANCE_ID_SUFFIX)) {
    const basePageFilterInstanceId =
      getBaseCustomPageFilterInstanceId(filterInstanceId);
    const customPagePagination = resetPagePagination(
      pagination,
      basePageFilterInstanceId
    );
    if (customPagePagination != null) {
      return customPagePagination;
    }
  }

  for (const [pageFilterInstanceId, pageFilters] of Object.entries(
    pagination
  )) {
    if (pageFilters == null) {
      continue;
    }

    const widgetFilterInstanceId = pageFilters[filterInstanceId]
      ? filterInstanceId
      : Object.keys(pageFilters).find(
          (widgetId) =>
            getTableFilterInstanceId(widgetId, pageFilterInstanceId) ===
            filterInstanceId
        );

    if (widgetFilterInstanceId == null) {
      continue;
    }

    const widgetPagination = pagination[pageFilterInstanceId];
    return {
      ...pagination,
      [pageFilterInstanceId]: {
        ...widgetPagination,
        [widgetFilterInstanceId]: {
          page: 0,
          perPage:
            widgetPagination?.[widgetFilterInstanceId]?.perPage ??
            DEFAULT_PAGE_SIZE,
        },
      },
    };
  }

  return pagination;
}

export const createTablePaginationSlice: StateCreator<
  StateSlices,
  [],
  [],
  TablePaginationSlice
> = (set) => {
  return {
    ...initialState,
    setPage(
      pageFilterInstanceId: string,
      widgetFilterInstanceId: string,
      page: number
    ) {
      set((state) => {
        return {
          pagination: {
            ...state.pagination,
            [pageFilterInstanceId]: {
              ...state.pagination[pageFilterInstanceId],
              [widgetFilterInstanceId]: {
                page,
                perPage:
                  state.pagination?.[pageFilterInstanceId]?.[
                    widgetFilterInstanceId
                  ]?.perPage ?? DEFAULT_PAGE_SIZE,
              },
            },
          },
        };
      });
    },
    setPerPage(
      pageFilterInstanceId,
      widgetFilterInstanceId: string,
      perPage: number
    ) {
      set((state) => {
        return {
          pagination: {
            ...state.pagination,
            [pageFilterInstanceId]: {
              ...state.pagination[pageFilterInstanceId],
              [widgetFilterInstanceId]: {
                page: 0,
                perPage,
              },
            },
          },
        };
      });
    },
    // sets pagination currentPage to 0 for all contents on a page or a given entity
    resetPagination(filterInstanceId: string) {
      set((state) => {
        const pagination = resetPaginationState(
          state.pagination,
          filterInstanceId
        );
        if (pagination === state.pagination) {
          return state;
        }
        return {
          pagination,
        };
      });
    },
    resetAllPagination() {
      set({ pagination: {} });
    },
  };
};
